import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useBrandManager } from "@/hooks/useBrandManager";
import { toast } from "sonner";

interface CreateAnnotationParams {
  imageBlob: Blob;
  title?: string;
  comment?: string;
  tags?: string[];
  pageRoute: string;
  containerId?: string;
}

export const useAnnotations = (pageRoute?: string) => {
  const { currentBrandId } = useBrandManager();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);

  // Fetch annotations
  const {
    data: annotations = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["os_annotations", currentBrandId, pageRoute],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) return [];

      let query = supabase
        .from("os_annotations")
        .select("*")
        .eq("user_id", session.session.user.id)
        .order("created_at", { ascending: false });

      if (currentBrandId) {
        query = query.eq("brand_id", currentBrandId);
      }

      if (pageRoute) {
        query = query.eq("page_route", pageRoute);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: true,
  });

  // Create annotation
  const createAnnotation = useCallback(
    async (params: CreateAnnotationParams): Promise<boolean> => {
      setIsCreating(true);

      try {
        const { data: session } = await supabase.auth.getSession();
        if (!session?.session?.user) {
          toast.error("You must be logged in to create annotations");
          return false;
        }

        const userId = session.session.user.id;

        // Generate unique filename
        const timestamp = Date.now();
        const filename = `${userId}/${timestamp}.png`;

        // Upload image to storage
        const { error: uploadError } = await supabase.storage
          .from("os-annotations")
          .upload(filename, params.imageBlob, {
            contentType: "image/png",
            upsert: false,
          });

        if (uploadError) {
          console.error("Upload error:", uploadError);
          toast.error("Failed to upload screenshot");
          return false;
        }

        // Get public URL
        const { data: urlData } = supabase.storage
          .from("os-annotations")
          .getPublicUrl(filename);

        // Insert annotation record
        const { error: insertError } = await supabase
          .from("os_annotations")
          .insert({
            user_id: userId,
            brand_id: currentBrandId || null,
            page_route: params.pageRoute,
            container_id: params.containerId || null,
            title: params.title || null,
            comment: params.comment || null,
            image_url: urlData.publicUrl,
            tags: params.tags || null,
          });

        if (insertError) {
          console.error("Insert error:", insertError);
          toast.error("Failed to save annotation");
          return false;
        }

        toast.success("Annotation saved successfully!");
        queryClient.invalidateQueries({ queryKey: ["os_annotations"] });
        return true;
      } catch (error) {
        console.error("Create annotation error:", error);
        toast.error("Failed to create annotation");
        return false;
      } finally {
        setIsCreating(false);
      }
    },
    [currentBrandId, queryClient]
  );

  // Delete annotation mutation
  const deleteAnnotationMutation = useMutation({
    mutationFn: async (annotationId: string) => {
      const { error } = await supabase
        .from("os_annotations")
        .delete()
        .eq("id", annotationId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Annotation deleted");
      queryClient.invalidateQueries({ queryKey: ["os_annotations"] });
    },
    onError: () => {
      toast.error("Failed to delete annotation");
    },
  });

  return {
    annotations,
    isLoading,
    error,
    refetch,
    createAnnotation,
    isCreating,
    deleteAnnotation: deleteAnnotationMutation.mutate,
    isDeleting: deleteAnnotationMutation.isPending,
  };
};
